import React, { useCallback, useLayoutEffect, useState } from "react";
import { UseSaveTour } from "../../../api/calls/Queries";
import { useQueryClient } from "@tanstack/react-query";
import { Group, Scene, TourData } from "../../../Types";
import WaypointExpIcon from "../../Icons/WaypointExpIcon";
import {
  useCurrentSceneStore,
  useEditingElementStore,
  useTourDataStore,
} from "../../../api/Zustand";
import { Trashbin } from "../../Icons/gui-elements-icons";
import { getLanguageFile } from "../../common/GetLanguageFile";

export default function GroupsEditing({ tourID }: { tourID: number }) {
  const { tourData } = useTourDataStore();

  const queryClient = useQueryClient();
  const { mutateAsync: saveTour, isPending: isSaving } = UseSaveTour();

  const { currentSceneID, setCurrentSceneID } = useCurrentSceneStore();

  const { setEditingElem } = useEditingElementStore();

  const [openAccordionElem, setOpen] = useState<number>(-1);

  const onGroupAdd = useCallback(() => {
    const randID = Math.floor(100000 + Math.random() * 900000);
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      groups: [
        ...tourData!.groups,
        {
          id: randID,
          name: "Nuovo gruppo",
          scenes: [],
          mainColor: "#1c64f2",
          textColor: "#fff",
        },
      ],
    });
    setOpen(randID);
  }, [queryClient, tourData, tourID]);
  return (
    <div className="right-side-panel-editor">
      <h3>{getLanguageFile("right_panel.groups")}</h3>

      {currentSceneID &&
        tourData?.scenes.find((one) => one.id === currentSceneID) && (
          <p
            className="group-back"
            onClick={() => {
              setEditingElem("scene", currentSceneID);
            }}
            style={{ display: "flex", gap: ".5rem", userSelect: "none" }}
          >
            <WaypointExpIcon
              width=".5rem"
              style={{ transform: "rotate(180deg)" }}
            />
            {getLanguageFile("right_panel.back_to")}{" "}
            <span>
              {tourData?.scenes.find((one) => one.id === currentSceneID)?.title}
            </span>
          </p>
        )}

      {tourData && (
        <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          {tourData.groups.map((group: Group) => (
            <AccordionElement
              key={group.id}
              group={group}
              openAccordionElem={openAccordionElem}
              setOpen={setOpen}
              tourData={tourData}
              tourID={tourID}
            />
          ))}
        </div>
      )}
      <button onClick={onGroupAdd}>
        + {getLanguageFile("right_panel.add_group")}
      </button>

      <hr
        style={{
          margin: 0,
          background: "#fffa",
          width: "100%",
          height: "1px",
          border: "none",
        }}
      />
      {tourData &&
      tourData!.scenes.find((scn: Scene) => scn.group === undefined) ? (
        <>
          {getLanguageFile("right_panel.scenes_no_group")}:
          <div
            style={{
              display: "inline-block",
            }}
          >
            {tourData!.scenes
              .filter((scn: Scene) => scn.group === undefined)
              .map((scn: Scene) => (
                <span
                  className="groupless-scene-select"
                  style={{
                    marginInline: ".25rem",
                    fontWeight: "200",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  key={scn.id}
                  onClick={() => {
                    setCurrentSceneID(scn.id as number);

                    setEditingElem("scene", scn.id);
                  }}
                >
                  {scn.title};
                </span>
              ))}
          </div>
        </>
      ) : null}

      <hr
        style={{
          margin: 0,
          background: "#fffa",
          width: "100%",
          height: "1px",
          border: "none",
        }}
      />

      {tourData && (
        <button
          id="save"
          onClick={() => saveTour(tourData)}
          disabled={isSaving}
        >
          {isSaving
            ? getLanguageFile("generics.saving")
            : getLanguageFile("generics.save")}
        </button>
      )}
    </div>
  );
}

const AccordionElement = (props: {
  group: Group;
  openAccordionElem: number;
  setOpen: React.Dispatch<React.SetStateAction<number>>;
  tourData: TourData;
  tourID: number;
}) => {
  const { group, openAccordionElem, setOpen, tourData, tourID } = props;

  const queryClient = useQueryClient();

  const { setCurrentSceneID } = useCurrentSceneStore();
  const { id, setEditingElem } = useEditingElementStore();

  const onTitleChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        groups: tourData.groups.map((gr: any) =>
          gr.id === group.id ? { ...gr, name: ev.target.value } : gr
        ),
      });
    },
    [group.id, queryClient, tourData, tourID]
  );

  const onMainColorChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        groups: tourData.groups.map((gr: any) =>
          gr.id === group.id ? { ...gr, mainColor: ev.target.value } : gr
        ),
      });
    },
    [group.id, queryClient, tourData, tourID]
  );

  const onTextColorChange = useCallback(
    (color: "#fff" | "#222") => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        groups: tourData.groups.map((gr: any) =>
          gr.id === group.id ? { ...gr, textColor: color } : gr
        ),
      });
    },
    [group.id, tourData, queryClient, tourID]
  );

  const onSceneRemoval = useCallback(
    (scnId: number) => {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: tourData.scenes.map((sc: Scene) =>
          sc.group === group.id ? { ...sc, group: undefined } : sc
        ),
        groups: tourData.groups.map((gr: Group) =>
          gr.id === group.id
            ? { ...gr, scenes: gr.scenes?.filter((sc: number) => sc !== scnId) }
            : gr
        ),
      });
    },
    [group.id, queryClient, tourData, tourID]
  );

  const onGroupDelete = useCallback(() => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      groups: [...tourData.groups.filter((gr: any) => gr.id !== group.id)],
      scenes: tourData.scenes.map((sc: Scene) =>
        sc.group === group.id ? { ...sc, group: undefined } : sc
      ),
    });
  }, [group.id, tourData, queryClient, tourID]);

  useLayoutEffect(() => {
    setOpen(id as number);
  }, [id, setOpen]);

  return (
    <div>
      <div
        className="group-accordion-header"
        onClick={() => {
          if (group.id === openAccordionElem) {
            setOpen(-1);
            return;
          }
          setOpen(group.id);
        }}
        style={{
          backgroundColor: group.mainColor,
          color: group.textColor,
          fontWeight: "800",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        id={openAccordionElem === group.id ? "open" : "closed"}
      >
        <span
          style={{
            opacity: openAccordionElem === group.id ? "0" : "1",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {group.name}
        </span>

        <WaypointExpIcon
          height={"1rem"}
          color={group.textColor}
          style={{
            transform: `rotate(${
              group.id === openAccordionElem ? -90 : 90
            }deg)`,
          }}
        />
      </div>
      {openAccordionElem === group.id && (
        <div
          className="group-accordion-content"
          style={{ background: group.mainColor + "50", color: group.textColor }}
        >
          <input
            type="text"
            defaultValue={group.name}
            onChange={onTitleChange}
            style={{ color: group.textColor }}
          />
          <label style={{ display: "flex", alignItems: "center" }}>
            {getLanguageFile("right_panel.bg_color")}
            <input
              type="color"
              value={group.mainColor}
              onChange={onMainColorChange}
            />
          </label>
          <div style={{ display: "flex", alignItems: "center" }}>
            {getLanguageFile("right_panel.txt_color")}
            <button
              id="color"
              className="white"
              disabled={group.textColor === "#fff"}
              onClick={() => onTextColorChange("#fff")}
            />
            <button
              id="color"
              className="black"
              disabled={group.textColor === "#222" ? true : false}
              onClick={() => onTextColorChange("#222")}
            />
          </div>

          {group.scenes && (
            <div>
              {getLanguageFile("right_panel.scenes")}:
              <br />
              <div
                style={{
                  display: "inline-block",
                }}
              >
                {group.scenes.map((scn: number) => (
                  <>
                    <span key={scn} style={{ width: "fit-content" }}>
                      <span
                        style={{
                          userSelect: "none",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setCurrentSceneID(scn);

                          setEditingElem("scene", scn);
                        }}
                      >
                        {
                          tourData.scenes.find((one: Scene) => one.id === scn)
                            ?.title
                        }
                      </span>
                      <button onClick={() => onSceneRemoval(scn)} id="small">
                        ✖
                      </button>
                    </span>
                  </>
                ))}
              </div>
              <br />
              <button id="delete" onClick={onGroupDelete}>
                {getLanguageFile("right_panel.delete_group")}
                <Trashbin />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
